import { Injectable } from "@angular/core";
import { IFilter } from "app/_models/IFilter";
import { IHeader } from "app/_models/IHeader";
import { AppComponent } from "app/app.component";
import sqliteParser from 'sqlite-parser';

@Injectable({ providedIn: 'root' })
export class FilterController {
  constructor(private app: AppComponent) { }

  //#region Individual Filters
  loadFilters(headers: IHeader[]): IFilter[] {
    let filters: IFilter[] = [];
    headers.forEach(header => {
      if (header.dataType == null) header.dataType = '';
      if (header.displayFormat == null) header.displayFormat = '';
      if (header.visible) filters.push(this.createFilter(header));
    })

    return filters;
  }

  createFilter(header: IHeader): IFilter {
    let format = 'string';
    if (header.dataType.toLowerCase() == 'timespan' && header.caption.toLowerCase().includes('time')) format = 'TIME';
    if ((header.dataType.toLowerCase() == 'datetime' || header.caption.toLowerCase().includes('date')) && header.dataType != 'Double') format = 'DATE';
    if (header.dataType.toLowerCase() == 'datetime' && header.displayFormat == '') format = 'DATETIME';
    if (header.dataType.toLowerCase() == 'boolean') format = 'CHECK';
    if (header.dataType == 'Byte[]') format = 'IMAGE';

    return {
      caption: header.caption,
      field: header.field,
      value: '',
      isNull: false,
      dataType: header.dataType,
      displayFormat: format,
      isActive: false,
      range: [],
      history: [],
      filteredHistory: [],
      focused: false,
      updated: false,
      ranges: {
        from: undefined,
        to: undefined,
        userDateFrom: '',
        userDateTo: '',
        serverDateFrom: '',
        serverDateTo: ''
      },
      kind: "IFilter",
      showHistory: false
    };
  }

  buildFilterString(filters: IFilter[]): string { 
    let conditions = [];
    filters.forEach(filter => {
      if (filter.isActive) {
        let filterString = '';
        filterString += `${filter.field} `;
        if (filter.isNull) {
          filterString += `IS NULL`;
        } else if (filter.displayFormat == 'TIME') {
          filterString += `>= '${filter.ranges.from}' and ${filter.field} <= '${filter.ranges.to}'`;
          if (filter.ranges) filter.value = filter.ranges.from;
        } else if (filter.displayFormat.includes('DATE')) {
          filterString += `>= '${filter.ranges.serverDateFrom}' and ${filter.field} <= '${filter.ranges.serverDateTo}'`;
        } else if (filter.displayFormat == 'CHECK') {
          filterString += `like '%${filter.value.toString() == 'true' ? 1 : 0}%'`;
        } else {
          filterString += `like '%${filter.value.trim()}%'`;
        }

        conditions.push(filterString);
      }
    })    

    return conditions.join(' and ');
  }

  clearFilter(filter: IFilter): IFilter {
    filter.updated = true;
    filter.value = '';
    filter.isNull = false;
    filter.isActive = false;
    filter.range = [];
    filter.ranges = {
      from: '',
      to: '',
      userDateFrom: '',
      userDateTo: '',
      serverDateFrom: '',
      serverDateTo: ''
    };
    filter.focused = false;
    filter.filteredHistory = filter.history;
    filter.showHistory = false;
    return filter;
  }

  clearAllFilters(filters: IFilter[]): IFilter[] {
    filters.forEach(filter => {
      if (filter.isActive) {        
        filter = this.clearFilter(filter);
        filter.updated = false;
      }
    })

    return filters;
  } 

  getUserDateFormat(selectedDate: string, userDateFormat: string): string {    
    let dateValues = selectedDate.split('/');
    if (userDateFormat == 'dd/MM/yyyy') return `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`;    
    if (userDateFormat == 'yyyy/MM/dd') return `${dateValues[2]}/${dateValues[0]}/${dateValues[1]}`;
    if (userDateFormat == 'MM/dd/yyyy') return `${dateValues[0]}/${dateValues[1]}/${dateValues[2]}`;    
  }
  //#endregion

  //#region Advanced Filter
  createEditableFilterString(filters: IFilter[]): string {
    let editableFilterString = '';
    filters.forEach(filter => {
      if (filter.isActive) {
        if (filter.isNull) {
          editableFilterString += `[${filter.caption}] IS NULL AND `;
        } else if ((filter.displayFormat == 'DATE' || filter.displayFormat == 'DATETIME') && filter.ranges) {
          editableFilterString +=
            `[${filter.caption}] >= '${filter.ranges.userDateFrom}' AND [${filter.caption}] <= '${filter.ranges.userDateTo}' AND `;
        } else if (filter.displayFormat == 'TIME' && filter.ranges) {
          editableFilterString +=
            `[${filter.caption}] >= '${filter.ranges.from}' AND [${filter.caption}] <= '${filter.ranges.to}' AND `;
        } else if (filter.displayFormat == 'CHECK') {          
          editableFilterString += `[${filter.caption}] = ${filter.value ? 1 : 0} AND `;
        } else if (filter.value.startsWith('!')) {
          editableFilterString += `[${filter.caption}] NOT LIKE '${filter.value.replace('!', '')}' AND `;
        } else if (filter.value.startsWith('%') || filter.value.endsWith('%')) {
          editableFilterString += `[${filter.caption}] LIKE '${filter.value}' AND `;
        } else {
          editableFilterString += `[${filter.caption}] LIKE '%${filter.value}%' AND `;
        }
      }
    })    

    if (editableFilterString.endsWith('AND ')) 
      editableFilterString = editableFilterString.slice(0, -4).trim();
    
    return editableFilterString;
  }

  validateFilterString(filterStringFromInput: string, headers: IHeader[], advancedQueryFromDialog: boolean): string {
    let regex = /\[([^\]]+)\]/g;
    let matches = [...filterStringFromInput.matchAll(regex)].map(match => match[1]);
    let totalOpeningBrackets = (filterStringFromInput.match(/\[/g) || []).length;
    let totalClosingBrackets = (filterStringFromInput.match(/\]/g) || []).length;
    if (totalOpeningBrackets !== totalClosingBrackets || matches.length === 0) {
      return this.app.translations.ADVFILTER_Error_UnpairedBrackets;   
    } 

    try {
      sqliteParser(`SELECT * FROM t WHERE ${filterStringFromInput}`);
    } catch (error) {
      return `${this.app.translations.ADVFILTER_Error_Error} - ${error.message}`;
    }

    if (advancedQueryFromDialog) return filterStringFromInput;

    let captions = new Set(headers.map(header => header.caption));
    if (!matches.every(field => captions.has(field))) 
      return this.app.translations.ADVFILTER_Error_InvalidFields;
    
    let captionToFieldMap = new Map(headers.map(obj => [obj.caption, obj.field]));
    let transformedWhereClause = filterStringFromInput.replace(regex, (_, caption) => {
      return captionToFieldMap.get(caption) || caption;
    });
    
    return transformedWhereClause.replace(/\blike\b/gi, 'LIKE');    
  }

  formatFilterStringDates(filterString: string, filters: IFilter[], userDateFormat: string): { userString: string, serverString: string, editableFilterStringError: string } {
    let conditions = this.parseConditions(filterString);
    let serverString = filterString;
    for (let i = 0; i < conditions.length; i++) {         
      let conditionMatch = conditions[i].match(/\[([^\]]+)]\s*(NOT LIKE|LIKE|=|>=|<=|<|>|BETWEEN|IS NULL)\s*(.*)?/i);
      if (conditionMatch) {
        let dayMonthYear = false, monthDayYear = false, yearMonthDay = false, sameDayMonth = false;
        let field = conditionMatch[1].trim();
        if (!conditionMatch[3]) conditionMatch[3] = '';
        let value = conditionMatch[3].trim().replace('\'', '').replace('\'', '');
        let filter = filters.find(f => f.caption === field);
        if (filter) {
          serverString = serverString.replace(new RegExp(`\\[${field}\\]`, 'g'), filter.field);

          if ((filter.displayFormat == 'DATE' || filter.dataType == 'DateTime') && !conditions[i].includes('NULL')) {
            if (value.includes(' ')) value = value.split(' ')[0];

            let match;
            if ((match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})$/))) {
              const [_, year, month, day] = match.map(Number);              
              if (this.isValidDate(year, month, day)) {
                yearMonthDay = true;
                sameDayMonth = month == day;
              }
            }

            if ((match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) {
              const [_, month, day, year] = match.map(Number);              
              if (this.isValidDate(year, month, day)) {
                monthDayYear = true;
                sameDayMonth = month == day;
              }
            }

            if ((match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) {
              const [_, day, month, year] = match.map(Number);              
              if (this.isValidDate(year, month, day) || day > 12) {
                dayMonthYear = true;
                sameDayMonth = month == day;
              }
            }
            
            let invalidDateFormatFound = false;
            if (monthDayYear && dayMonthYear) monthDayYear = false;
            if (yearMonthDay && userDateFormat != 'yyyy/MM/dd' && !sameDayMonth) invalidDateFormatFound = true;
            if (monthDayYear && userDateFormat != 'MM/dd/yyyy' && !sameDayMonth) invalidDateFormatFound = true;
            if (dayMonthYear && userDateFormat != 'dd/MM/yyyy' && !sameDayMonth) invalidDateFormatFound = true;
            if (!yearMonthDay && !monthDayYear && !dayMonthYear) invalidDateFormatFound = true;

            if (invalidDateFormatFound) 
              return { userString: '', serverString: '', editableFilterStringError: `Invalid date found. Your chosen date format is: ${userDateFormat}` };
            
            let dateValues = value.split('/');
            if (userDateFormat == 'dd/MM/yyyy')               
              serverString = serverString.replace(value, `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`);            

            if (userDateFormat == 'yyyy/MM/dd')               
              serverString = serverString.replace(value, `${dateValues[1]}/${dateValues[2]}/${dateValues[0]}`);            
          }
        }
      }
    }

    let userString = filterString;
    return { userString, serverString, editableFilterStringError: '' };
  }

  parseConditions(input: string): string[] {    
    let matches = input.match(/\([^()]+\)|\[[^\[\]]+\]\s+(?:NOT\s+LIKE|LIKE|=|>|<|>=|<=)\s*'(?:[^']*)'|\[[^\[\]]+\]\s+(?:NOT\s+LIKE|LIKE|=|>|<|>=|<=)\s*\d+|\[[^\[\]]+\]\s+IS\s+NULL|AND|OR/g);
    return matches ? matches.map(match => match.trim()) : [];
  }

  isValidDate(year: number, month: number, day: number): boolean {
    if (month < 1 || month > 12 || day < 1) return false;
    const daysInMonth = new Date(year, month, 0).getDate();
    return day <= daysInMonth;
  }  
  //#endregion  
}